import type { Checker } from "./mapcheck";
import { halfX, halfZ, surfaceAt, type Level } from "./level";

// Graffiti to wash off (#151): tags sprayed about the park for the power
// washer. A map file's `graffiti` list places them: on the floor (or a deck
// or ramp) at (x, z), or, with a `y`, up on the fence nearest (x, z), its
// middle that high over the floor there. The jet scrubs a tag while it hits
// within the tag's radius; after `TAG_WASH_TIME` of that it's clean
// (`PackState.cleaned`, a `clean` event) and stays clean until the map
// reloads. Sim- and three-free; `render/graffiti.ts` draws them.

/** At most this many tags on a map. */
export const MAX_TAGS = 8;

/** A tag's size across (m): the smallest and biggest a map file can have. */
export const TAG_SIZE = { min: 0.8, max: 3 } as const;

/** A tag in a map file: where, how big across, and (on the fence) how high its middle is. */
export interface MapTag {
  x: number;
  z: number;
  size: number;
  /** On the fence nearest (x, z), this high over the floor there (m); left out, on the ground at (x, z). */
  y?: number;
}

/** A tag as the sim has it: its middle on the surface, the surface's outward normal, and its radius. */
export interface Tag {
  x: number;
  y: number;
  z: number;
  nx: number;
  ny: number;
  nz: number;
  r: number;
}

export function checkGraffiti(c: Checker, v: unknown, path: string, bounds: number): void {
  if (!Array.isArray(v) || v.length > MAX_TAGS) {
    c.fail(path, `expected a list of at most ${MAX_TAGS} tags`);
    return;
  }
  v.forEach((e, i) => {
    const at = `${path}[${i}]`;
    const o = c.obj(e, at);
    if (!o) return;
    c.only(o, ["x", "z", "size", "y"], at);
    c.num(o, "x", at, { min: -bounds, max: bounds });
    c.num(o, "z", at, { min: -bounds, max: bounds });
    c.num(o, "size", at, TAG_SIZE);
    c.num(o, "y", at, { min: 0.3, max: 3, optional: true });
  });
}

/** Puts a map file's tag on the level: on the ground there, or snapped onto the nearest fence line. */
export function buildTag(t: MapTag, level: Level): Tag {
  const r = t.size / 2;
  if (t.y === undefined) {
    const s = surfaceAt(level, t.x, t.z);
    const l = Math.hypot(s.gx, 1, s.gz);
    return { x: t.x, y: s.h, z: t.z, nx: -s.gx / l, ny: 1 / l, nz: -s.gz / l, r };
  }
  const hx = halfX(level);
  const hz = halfZ(level);
  let x = t.x;
  let z = t.z;
  let nx = 0;
  let nz = 0;
  if (hx - Math.abs(t.x) <= hz - Math.abs(t.z)) {
    nx = -Math.sign(t.x) || -1;
    x = -nx * hx;
  } else {
    nz = -Math.sign(t.z) || -1;
    z = -nz * hz;
  }
  // The floor just inside the fence.
  const floor = surfaceAt(level, x + nx * 0.05, z + nz * 0.05).h;
  return { x, y: floor + t.y, z, nx, ny: 0, nz, r };
}

/** The tags not yet `cleaned` that a jet hitting (x, y, z) scrubs: within their radius. */
export function tagsUnder(tags: readonly Tag[], cleaned: ReadonlySet<number>, x: number, y: number, z: number): number[] {
  const out: number[] = [];
  tags.forEach((t, i) => {
    if (cleaned.has(i)) return;
    if ((t.x - x) ** 2 + (t.y - y) ** 2 + (t.z - z) ** 2 <= t.r * t.r) out.push(i);
  });
  return out;
}
